// src/components/FiltroHome.jsx
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { IMaskInput } from 'react-imask';
import FiltroLocalAtuacao from './FiltroLocalAtuacao';
import {
  searchBairroSuggestions,
  getGroups, // p/ montar ownerIds (me + sócios + parceiros)
} from '../services/searchClient';

const normRef = (v = '') => String(v).toUpperCase().replace(/\s+/g, '').trim();

/* Debounce simples para callbacks */
function useDebouncedCallback(fn, delay = 250) {
  const t = useRef();
  return useCallback(
    (...args) => {
      clearTimeout(t.current);
      t.current = setTimeout(() => fn(...args), delay);
    },
    [fn, delay]
  );
}

/* Hook: resolve ownerIds a partir dos toggles + getGroups() */
function useOwnerIds({ origemMeus, origemSocios, origemParceiros }) {
  const [groups, setGroups] = useState({ me: null, socios: [], parceiros: [] });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const g = await getGroups();
        if (alive) setGroups(g || { me: null, socios: [], parceiros: [] });
      } catch {
        if (alive) setGroups({ me: null, socios: [], parceiros: [] });
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const ownerIds = useMemo(() => {
    const ids = [];
    const useMe = origemMeus !== false; // undefined => true
    const useSoc = origemSocios !== false;
    const usePar = origemParceiros !== false;

    if (useMe && groups.me) ids.push(groups.me);
    if (useSoc && Array.isArray(groups.socios)) ids.push(...groups.socios);
    if (usePar && Array.isArray(groups.parceiros))
      ids.push(...groups.parceiros);

    // fallback: se nada marcado, considera todos
    if (ids.length === 0) {
      if (groups.me) ids.push(groups.me);
      if (Array.isArray(groups.socios)) ids.push(...groups.socios);
      if (Array.isArray(groups.parceiros)) ids.push(...groups.parceiros);
    }

    // unique
    return Array.from(new Set(ids.filter(Boolean)));
  }, [groups, origemMeus, origemSocios, origemParceiros]);

  return ownerIds;
}

const FiltroHome = ({ filtros, setFiltros, onBuscar }) => {
  // --- ownerIds para filtrar sugestões conforme Meus/Sócios/Parceiros ---
  const ownerIds = useOwnerIds({
    origemMeus: filtros?.origemMeus,
    origemSocios: filtros?.origemSocios,
    origemParceiros: filtros?.origemParceiros,
  });

  // --- estado/autocomplete para Bairro ---
  const [bairroQuery, setBairroQuery] = useState(filtros?.bairro || '');
  const [sugestoes, setSugestoes] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  const boxRef = useRef(null);

  useEffect(() => {
    setBairroQuery(filtros?.bairro || '');
  }, [filtros?.bairro]);

  // Buscar sugestões (debounced) — inclui ownerIds e localização
  const fetchSugestoes = useDebouncedCallback(
    async (q) => {
      const term = (q || '').trim();
      if (term.length < 2) {
        setSugestoes([]);
        setAberto(false);
        return;
      }
      try {
        const hits = await searchBairroSuggestions(term, {
          publicado: true,
          cidade: filtros?.cidade || undefined,
          estado: filtros?.estado || undefined,
          ownerIds, // <- respeita Meus/Sócios/Parceiros
        });
        setSugestoes(hits);
        setAberto(hits.length > 0);
        setFocusIdx(-1);
      } catch (e) {
        console.error('Erro ao buscar sugestões de bairro:', e);
        setSugestoes([]);
        setAberto(false);
      }
    },
    250 // delay
  );

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    const onDocClick = (e) => {
      if (!boxRef.current?.contains(e.target)) setAberto(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const selecionarBairro = (itemOrLabel) => {
    const item =
      typeof itemOrLabel === 'string'
        ? { label: itemOrLabel, bairro: itemOrLabel }
        : itemOrLabel || {};

    const novoBairro = item.bairro || item.label || '';
    const novaCidade = item.cidade || '';
    const novoEstado = item.estado || '';

    setBairroQuery(novoBairro);
    setAberto(false);
    setSugestoes([]);

    setFiltros((prev) => ({
      ...prev,
      bairro: novoBairro,
      // Se a sugestão trouxe cidade/UF (facet composto), autocompleta:
      cidade: novaCidade || prev.cidade || '',
      estado: novoEstado || prev.estado || '',
    }));

    // Opcional: dispara a busca ao selecionar
    onBuscar?.();
  };

  // --------- handlers gerais ---------
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Dormitórios: 2/3 = exato | 4+ = mínimo 4
    if (name === 'dormitoriosFiltro') {
      if (value === '') {
        setFiltros((prev) => ({
          ...prev,
          dormitoriosFiltro: '',
          quartosExatos: '',
          minQuartos: '',
        }));
      } else if (value === '4+') {
        setFiltros((prev) => ({
          ...prev,
          dormitoriosFiltro: '4+',
          quartosExatos: '',
          minQuartos: 4,
        }));
      } else {
        setFiltros((prev) => ({
          ...prev,
          dormitoriosFiltro: value, // '2' ou '3'
          quartosExatos: Number(value), // exato
          minQuartos: '',
        }));
      }
      return;
    }

    // 🔗 SINCRONIZAÇÃO: código -> ref (para Algolia)
    if (name === 'codigo') {
      setFiltros((prev) => ({
        ...prev,
        codigo: value,
        ref: normRef(value), // <-- usado nos hooks Algolia
      }));
      return;
    }

    // Campo de bairro (com autocomplete)
    if (name === 'bairro') {
      setBairroQuery(value);
      fetchSugestoes(value);
      setFiltros((prev) => ({ ...prev, bairro: value }));
      return;
    }

    setFiltros((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleLocalizacaoChange = (novaLocalizacao) => {
    setFiltros((prev) => ({
      ...prev,
      cidade: novaLocalizacao.cidade || '',
      estado: novaLocalizacao.estado || '',
    }));
  };

  // defaults dos checkboxes de origem
  useEffect(() => {
    setFiltros((prev) => ({
      ...prev,
      origemMeus: prev?.origemMeus ?? true,
      origemSocios: prev?.origemSocios ?? true,
      origemParceiros: prev?.origemParceiros ?? true,
    }));
  }, [setFiltros]);

  // Enter geral para buscar (fora do dropdown)
  const onKeyDownEnter = (e) => {
    if (e.key === 'Enter' && !aberto) onBuscar?.();
  };

  // Teclas no campo de bairro quando o dropdown está aberto
  const onKeyDownBairro = (e) => {
    if (!aberto) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIdx((i) => Math.min(i + 1, sugestoes.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = sugestoes[focusIdx] || sugestoes[0];
      if (item) selecionarBairro(item);
    } else if (e.key === 'Escape') {
      setAberto(false);
    }
  };

  return (
    <div
      className="bg-white p-6 rounded-2xl shadow-sm border space-y-4"
      onKeyDown={onKeyDownEnter}
    >
      <h2 className="text-base font-semibold text-black">Filtros</h2>

      <FiltroLocalAtuacao
        filtros={filtros}
        setFiltros={setFiltros}
        onLocalizacaoChange={handleLocalizacaoChange}
      />

      <div className="pt-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Mostrar apenas imóveis de
        </label>
        <div className="space-y-2 text-sm text-gray-800">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              name="origemMeus"
              checked={!!filtros.origemMeus}
              onChange={handleChange}
            />
            Meus imóveis
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              name="origemSocios"
              checked={!!filtros.origemSocios}
              onChange={handleChange}
            />
            Sócios
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              name="origemParceiros"
              checked={!!filtros.origemParceiros}
              onChange={handleChange}
            />
            Parceiros
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Tipo do Imóvel
        </label>
        <select
          name="tipo"
          value={filtros.tipo || ''}
          onChange={handleChange}
          className="w-full border border-gray-300 rounded-xl p-2 text-sm text-black bg-white"
        >
          <option value="">Todos</option>
          <option>Casa</option>
          <option>Apartamento</option>
          <option>Apartamento na planta</option>
          <option>Kitnet</option>
          <option>Terreno / Área</option>
          <option>Sala comercial</option>
          <option>Ponto comercial</option>
          <option>Galpão / Barracão</option>
          <option>Empreendimentos</option>
          <option>Chácara</option>
          <option>Fazenda</option>
        </select>
      </div>

      {/* Bairro com autocomplete */}
      <div className="relative" ref={boxRef}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Bairro
        </label>
        <input
          type="text"
          name="bairro"
          value={bairroQuery}
          onChange={handleChange}
          onKeyDown={onKeyDownBairro}
          onFocus={() => (sugestoes.length ? setAberto(true) : null)}
          placeholder="Digite o bairro (ex.: Portal...)"
          autoComplete="off"
          className="w-full border border-gray-300 rounded-xl p-2 text-sm text-black"
        />
        {aberto && sugestoes.length > 0 && (
          <div className="absolute z-20 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg max-h-64 overflow-auto">
            {sugestoes.map((s, idx) => (
              <button
                key={`${s.label}-${idx}`}
                type="button"
                onMouseDown={(e) => e.preventDefault()} // evita blur antes do click
                onClick={() => selecionarBairro(s)}
                className={`w-full text-left px-3 py-2 hover:bg-gray-50 ${
                  idx === focusIdx ? 'bg-gray-100' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>{s.label}</span>
                  {typeof s.count === 'number' && (
                    <span className="text-xs text-gray-400">{s.count}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Faixa de preço com IMask numérico */}
      <div className="flex gap-2">
        <IMaskInput
          name="minPreco"
          mask={Number}
          unmask={'typed'}
          scale={0}
          signed={false}
          thousandsSeparator="."
          radix=","
          mapToRadix={['.']}
          lazy={false}
          prefix="R$ "
          inputMode="numeric"
          placeholder="Valor de"
          value={filtros.minPreco ?? ''}
          onAccept={(val) =>
            setFiltros((prev) => ({ ...prev, minPreco: val || '' }))
          }
          className="w-1/2 border border-gray-300 rounded-xl p-2 text-sm text-black"
        />
        <IMaskInput
          name="maxPreco"
          mask={Number}
          unmask={'typed'}
          scale={0}
          signed={false}
          thousandsSeparator="."
          radix=","
          mapToRadix={['.']}
          lazy={false}
          prefix="R$ "
          inputMode="numeric"
          placeholder="Até"
          value={filtros.maxPreco ?? ''}
          onAccept={(val) =>
            setFiltros((prev) => ({ ...prev, maxPreco: val || '' }))
          }
          className="w-1/2 border border-gray-300 rounded-xl p-2 text-sm text-black"
        />
      </div>

      {/* Dormitórios */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Dormitórios
        </label>
        <select
          name="dormitoriosFiltro"
          value={filtros.dormitoriosFiltro || ''}
          onChange={handleChange}
          className="w-full border border-gray-300 rounded-xl p-2 text-sm text-black bg-white"
        >
          <option value="">Todos</option>
          <option value="2">2 dormitórios</option>
          <option value="3">3 dormitórios</option>
          <option value="4+">4 ou +</option>
        </select>
      </div>

      <div>
        <input
          type="text"
          name="codigo"
          value={filtros.codigo || ''}
          onChange={handleChange}
          placeholder="Busque pelo código..."
          className="w-full border border-gray-300 rounded-xl p-2 text-sm text-black"
        />
      </div>

      <button
        type="button"
        onClick={onBuscar}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-xl font-semibold transition"
      >
        Buscar Imóveis
      </button>
    </div>
  );
};

export default FiltroHome;
